import { inject } from '@angular/core';
import { ResolveFn } from '@angular/router';
import { forkJoin, map, Observable, of, tap } from 'rxjs';
import {
  ActionLogArea,
  ActionLogItem,
  ActionLogSearchRequest,
} from 'src/app/shared/models/action-log.model';
import type { ClientApplication } from 'src/app/shared/models/applications.model';
import type { ClientAddressHistory } from 'src/app/shared/models/client/address.model';
import type { ClientPersonalDetails } from 'src/app/shared/models/client/personal.model';
import { ActionLogService } from 'src/app/shared/services/action-log/action-log.service';
import { ApplicationsService } from 'src/app/shared/services/applications/applications.service';
import { ClientService } from 'src/app/shared/services/client/client.service';
import { ActionLogStore } from 'src/app/shared/stores/action-log/action-log.store';
import { ApplicationsStore } from 'src/app/shared/stores/applications/applications.store';
import { ClientStore } from 'src/app/shared/stores/client/client.store';

export const openBankingResolver: ResolveFn<boolean> = () => {
  const clientService = inject(ClientService);
  const applicationsService = inject(ApplicationsService);
  const actionLogService = inject(ActionLogService);
  const clientStore = inject(ClientStore);
  const applicationsStore = inject(ApplicationsStore);
  const actionLogStore = inject(ActionLogStore);

  const hasPersonalDetails = !!clientStore.personalDetails?.();
  const hasAddressHistory = !!clientStore.addressHistory?.()?.length;
  const hasApplications = !!applicationsStore.applications?.()?.length;
  const hasActionLogs = !!actionLogStore.actions?.()?.length;

  if (hasPersonalDetails && hasAddressHistory && hasApplications && hasActionLogs) {
    return of(true);
  }

  const client = clientStore.client?.();

  if (!client?.clientId) {
    throw new Error('Client ID is required for open banking data');
  }

  const dataLoaders: { [key: string]: Observable<unknown> } = {};

  if (!hasPersonalDetails) {
    dataLoaders['personalDetails'] = clientService.getPersonalDetails(client.clientId);
  }

  if (!hasAddressHistory) {
    dataLoaders['addressHistory'] = clientService.getAddressHistory(client.clientId);
  }

  if (!hasApplications) {
    dataLoaders['applications'] = applicationsService.get(client.clientId);
  }

  if (!hasActionLogs) {
    const noFilterByCaseId = '';

    const searchRequest: ActionLogSearchRequest = {
      organisationId: client.brokerageId?.toString() || '',
      area: ActionLogArea.CaseUserAction,
      externalReference1: noFilterByCaseId,
      externalReference2: client.clientId.toString(),
    };

    dataLoaders['actionLogs'] = actionLogService.search(searchRequest);
  }

  return forkJoin(dataLoaders).pipe(
    tap((data) => {
      if (data['personalDetails']) {
        clientStore.setPersonalDetails(data['personalDetails'] as ClientPersonalDetails);
      }
      if (data['addressHistory']) {
        clientStore.setAddressHistory(data['addressHistory'] as ClientAddressHistory);
      }
      if (data['applications']) {
        applicationsStore.setApplications(
          client.clientId,
          data['applications'] as ClientApplication[]
        );
      }
      if (data['actionLogs']) {
        actionLogStore.setActions(data['actionLogs'] as ActionLogItem[]);
      }
    }),
    map(() => true)
  );
};
